import React, { useEffect, useRef } from 'react'
import { notification } from 'antd'
import './session-warning-notification.scss'
import { AppState } from '../../stores/app'
import { AppService } from '../../services'
import { useService } from '@pushly/aqe/lib/hooks'
import { observer } from 'mobx-react'

export const SessionWarningNotification = observer(() => {
    const appState = useService(AppState)
    const appSvc = useService(AppService)
    const notificationKey = useRef('session-warning')
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    const formatTimeRemaining = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60)
        const remainingSeconds = Math.min(59, Math.round(seconds % 60))

        if (minutes > 0) {
            return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
        }
        return `${remainingSeconds} seconds`
    }

    const openNotification = () => {
        notification.warning({
            key: notificationKey.current,
            message: (
                <div className="session-warning-notification-header">
                    <span className="session-warning-notification-title">Session Expiring Soon</span>
                </div>
            ),
            description: (
                <div className="session-warning-notification-content">
                    <div className="session-warning-notification-message">
                        Your session will expire in {formatTimeRemaining(appSvc.sessionSecondsRemaining)}
                        <div>Please save your work to avoid losing any changes.</div>
                    </div>
                </div>
            ),
            duration: 0, // Don't auto-close
            placement: 'topRight',
            className: 'session-warning-notification',
            onClose: handleDismiss,
        })
    }

    const updateNotification = () => {
        notification.warning({
            key: notificationKey.current,
            message: (
                <div className="session-warning-notification-header">
                    <span className="session-warning-notification-title">Session Expiring Soon</span>
                </div>
            ),
            description: (
                <div className="session-warning-notification-content">
                    <div className="session-warning-notification-message">
                        Your session will expire in {formatTimeRemaining(appSvc.sessionSecondsRemaining)}
                        <div>Please save your work to avoid losing any changes.</div>
                    </div>
                </div>
            ),
            duration: 0,
            placement: 'topRight',
            className: 'session-warning-notification',
            onClose: handleDismiss,
        })
    }

    const handleDismiss = () => {
        notification.destroy()
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
        appSvc.dismissSessionWarning()
    }

    useEffect(() => {
        if (appSvc.shouldShowSessionWarning && appState.isAuthenticated) {
            openNotification()

            // Update the countdown every second
            intervalRef.current = setInterval(() => {
                if (appSvc.shouldShowSessionWarning) {
                    updateNotification()
                } else {
                    notification.destroy()
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current)
                        intervalRef.current = null
                    }
                }
            }, 1000)
        } else {
            notification.destroy()
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [appSvc.shouldShowSessionWarning, appState.isAuthenticated])

    return null
})
